import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import API, { TokenData, UserLogin } from './API';
import { Breadcrumb, SiteRoute } from './SiteClasses';
import { joinUrls, nowUnix } from './SiteUtil';
import { useLocation } from 'react-router-dom';

type HasAccessFn = (a: number | number[], use_or?: boolean, ignore_super?: boolean)=>boolean;


const UserAuthContext = createContext<{
	login: boolean,
	user: UserLogin["user"],
	access: UserLogin["access"],
	hasAccess: HasAccessFn,
	logout: ()=>void,
	refresh: ()=>void,
}>({
	login: false,
	user: null,
	access: null,
	hasAccess: ()=>false,
	logout: ()=>null,
	refresh: ()=>null,
});
export var useUser = () => useContext(UserAuthContext);

export var UserProvider = (props: PropsWithChildren)=>{
	var [user, setUser] = useState<UserLogin>(JSON.parse(localStorage.getItem('user_data') || null));
	var [token, setToken] = useState<TokenData>(JSON.parse(localStorage.getItem('access_token') || null));
	var now = nowUnix();
	var is_expired = !token || (token.expires<=(now+30));

	var updateData = ()=>{
		try{
			var d = JSON.parse(localStorage.getItem('user_data'));
			setUser(d);
			return d as UserLogin;
		}catch(e){}
	}

	var updateToken = ()=>{
		try{
			var d = JSON.parse(localStorage.getItem('access_token'));
			setToken(d);
			return d as TokenData;
		}catch(e){}
	}

	var refresh = ()=>{
		API.getLogin(true);
	}

	var hasAccess = (a: number | number[], use_or: boolean=true, ignore_super: boolean=false)=>{
		if(!user || !user.user || user.access===null || is_expired) return false;
		// TEMPORAL: verificacion deshabilitada. Descomentar lo de abajo antes de subir.
		return true;
		// if((user.user.superadmin && ignore_super===false) || typeof a==='undefined' || (a as number[]).length==0) return true;
		// if(Array.isArray(a)){
		// 	var acc = (a as any) as number[], valid = [];
		// 	for(var i of acc){
		// 		var ha = user.access.indexOf(i);
		// 		if(ha!=-1){
		// 			if(use_or) return true;
		// 			valid.push(i);
		// 		}
		// 	}
		// 	return valid.length>=a.length;
		// }else{
		// 	return user.access && user.access.indexOf(a)!=-1;
		// }
	}

	var logout = ()=>{
		API.logout();
	}

	useEffect(()=>{
		refresh();
		window.addEventListener('user_data', updateData);
		window.addEventListener('user_token', updateToken);
		return ()=>{
			window.removeEventListener('user_data', updateData);
			window.removeEventListener('user_token', updateToken);
		}
	}, []);

	return <UserAuthContext.Provider value={{
		login: !is_expired && !!user,
		user: is_expired ? null : user?.user,
		access: is_expired ? null : user?.access,
		hasAccess,
		logout,
		refresh,
	}}>
		{props.children}
	</UserAuthContext.Provider>
}


var BreadcrumbsContext = createContext<{
	breadcrumbs: Breadcrumb[],
	sidebar: JSX.Element,
	setTitle: (title: string)=>void,
}>(null);

export var useBreadcrumbs = ()=>useContext(BreadcrumbsContext);

export var BreadcrumbsProvider = (props: PropsWithChildren & { routes: SiteRoute[], suffix?: string })=>{
	var location = useLocation();
	var [titleHistory, setTitleHistory] = useState<{ pathname: string, title: string }[]>([]);

	useEffect(()=>{
		var suffix = props.suffix || 'Arema CRM';
		var title = titleHistory.find(a=>a.pathname===location.pathname);
		document.title = (title?.title) ? `${title.title} - ${suffix}` : suffix;
	}, [location, titleHistory]);

	var changeTitle = (title: string)=>{
		if(!location.key) return;
		var hs = titleHistory.filter(a=>a.pathname!==location.pathname);
		hs.push({ pathname: location.pathname, title });
		setTitleHistory(hs);
	}

	var isCurrentUrl = (loc: string, path: string)=>{
		var paths = path.split('/').filter(a=>!!a && a.length>0);
		var reg = paths.length === 0
			? new RegExp(`^/?$`)
			: new RegExp(`^/${paths.map(p=>{
				if(p.startsWith(':')) return `(?<${p.substring(1)}>[A-z0-9_]+)`;
				return p;
			}).join('/')}/?`);
		return reg.exec(loc);
	}

	var getBreadcrumb = (loc: string, parent: string, routes: SiteRoute[]): Breadcrumb[]=>{
		var bread: Breadcrumb[] = [];
		for(var i of routes){
			var path = joinUrls(parent, i.path);
			var reg_res = isCurrentUrl(loc, path);
			if(reg_res){
				if(!i.ignore){
					bread.push({
						text: typeof i.title==='string' ? i.title : i.title(reg_res.groups),
						url: path,
						sidebar: i.sidebar,
						iconName: i.iconName,
					});
				}
				if(i.children) bread.push(...getBreadcrumb(loc, path, i.children));
				break;
			}
		}
		return bread;
	}

	var getSidebar = (loc: string, parent: string, routes: SiteRoute[]): JSX.Element=>{
		var sidebar: JSX.Element;
		for(var i of routes){
			var path = joinUrls(parent, i.path);
			if(isCurrentUrl(loc, path)){
				if(i.sidebar) sidebar = i.sidebar;
				if(i.children){
					var ch = getSidebar(loc, path, i.children);
					if(ch) sidebar = ch;
				}
				break;
			}
		}
		return sidebar;
	}

	var breadcrumbs = useMemo(()=> getBreadcrumb(location.pathname, '/', props.routes), [location.pathname]);
	var sidebar = useMemo(()=> getSidebar(location.pathname, '/', props.routes), [location.pathname]);

	return <BreadcrumbsContext.Provider value={{ breadcrumbs, sidebar, setTitle: changeTitle }}>
		{props.children}
	</BreadcrumbsContext.Provider>
}
